import type { ManagerGetCampaignModel, ContractorGetCampaignModel } from 'entities/campaign';
import { SchedulingDayChangeable, SchedulingDay } from 'entities/campaign';
import { useEffect } from 'react';
import { getDataAutoTestAttributes } from 'shared/lib/data-auto-test';
import { getKeys } from 'shared/lib/object';
import { timezoneOptions } from 'shared/lib/timezones';
import { Button } from 'shared/ui/Button';
import { useModalsActions } from 'shared/ui/SideModal';
import { Tooltip } from 'shared/ui/Tooltip';

import { useGetScheduleTimeRange } from '../lib/useGetScheduleTimeRange';

import { ScheduleModal } from './ScheduleModal';
import { PendingScheduleTooltip } from './PendingScheduleTooltip';

const BLOCKING_MANAGE_BTN_MESSAGE = 'Schedule editing is possible from 8:00 AM EST Monday to 10:00 AM EST Friday';

interface LeadsReceivingTimeProps {
  campaign: ManagerGetCampaignModel | ContractorGetCampaignModel;
  appliesAt: string | null;
  isShowClock: boolean;
  isBlockingTime: boolean;
}

export function CallCenterTime({ campaign, isShowClock, isBlockingTime, appliesAt }: LeadsReceivingTimeProps) {
  const { open, closeAllBesidesFirst } = useModalsActions();
  const schedule = campaign.schedule;

  useEffect(() => {
    if (isBlockingTime) {
      closeAllBesidesFirst();
    }
  }, [isBlockingTime]);

  const { getTimeOptions, isLoading } = useGetScheduleTimeRange({ timeZone: campaign.timezone });

  if (!schedule) {
    return null;
  }

  const timeZoneValue = timezoneOptions.find((x) => x.id === campaign.timezone)?.text;

  return (
    <>
      <div className="my-8 border-t border-slate-100" />
      <div className="mb-4 flex items-center justify-between">
        <div className="flex">
          <div
            className="text-lg font-semibold"
            {...getDataAutoTestAttributes(['title-lead-delivery-schedule']).attributes}
          >
            Call center hours
          </div>
          <PendingScheduleTooltip
            appliesAt={appliesAt}
            isShowClock={isShowClock}
          />
        </div>
        <Tooltip
          isDisabled={!isBlockingTime}
          placement="bottom-end"
          content={BLOCKING_MANAGE_BTN_MESSAGE}
        >
          <Button
            disabled={isBlockingTime || isLoading}
            dataTestId="manageSchedulingButton"
            type="button"
            onClick={() => {
              open(
                <ScheduleModal
                  campaign={campaign}
                  appliesAt={appliesAt}
                  isShowClock={isShowClock}
                >
                  {!isLoading &&
                    getKeys(schedule).map((day, index) => (
                      <SchedulingDayChangeable
                        index={index}
                        timeOptions={getTimeOptions(day)}
                        key={day}
                        name={day}
                        period={schedule[day]}
                        schedule={schedule}
                        className="mt-2"
                        allowCopySchedule={false}
                      />
                    ))}
                </ScheduleModal>
              );
            }}
            className="w-24 px-5"
            variant="outline"
            size="medium"
            {...getDataAutoTestAttributes(['button-manage-lead-schedule'])}
          >
            Manage
          </Button>
        </Tooltip>
      </div>
      <div className="mb-2 text-xs text-slate-700">{timeZoneValue}</div>
      {getKeys(schedule).map((day, index) => (
        <SchedulingDay
          index={index}
          key={day}
          name={day}
          period={schedule[day]}
          className="mt-2"
        />
      ))}
    </>
  );
}
